/**
 * CsvPreview — the "Rich" view of a CSV/TSV file (or a fenced ```csv block) inside a
 * RichCodeBlock. Renders the delimited text as a brand-tinted data grid: sticky header,
 * sticky row-number gutter, zebra rows, numeric columns right-aligned.
 *
 * Parsing reuses the SheetJS build the xlsx DocumentViewer already lazy-loads (same
 * chunk, no extra dependency). `raw: true` keeps every cell as its exact source text
 * ("00123" stays "00123", "12/31/2024" is not turned into a date serial) and SheetJS
 * auto-detects the delimiter (comma, semicolon, tab, pipe) and handles quoted fields
 * with embedded commas/newlines/escaped quotes.
 *
 * Caps mirror xlsxStyledRenderer (MAX_CSV_ROWS / MAX_CSV_COLS) so a pathological file
 * never freezes the tab; a footer says what was cut. Styles live in RichCodeBlock.css
 * (`.rich-code-block-csv-*`), themed by the parent `.rich-code-block.light|dark`.
 */
import React, { useEffect, useState } from 'react';

export const MAX_CSV_ROWS = 5000;
export const MAX_CSV_COLS = 256;
// Cells are React-rendered (unlike the xlsx HTML string), so a wide file lowers the row
// cap: 250k cells ≈ 5000 rows × 50 cols, or ~975 rows at the 256-column limit.
export const MAX_CSV_CELLS = 250000;

// Plain numbers, thousands separators, currency prefixes, percents, scientific notation.
const NUMERIC_RE = /^[-+]?[$€£¥]?\s?(\d{1,3}(,\d{3})+|\d+)(\.\d+)?(e[-+]?\d+)?%?$/i;
// A column is "numeric" (right-aligned) when at least this share of its non-empty cells parse.
const NUMERIC_COLUMN_SHARE = 0.8;

/**
 * Parse delimited text into a rectangular-ish grid of strings (rows x cells).
 * @param text  file content (BOM tolerated)
 * @param XLSX  the SheetJS module (injected so the caller controls the lazy import)
 * @returns string[][] with trailing blank rows removed; [] for an empty file
 */
export function parseCsvGrid(text, XLSX) {
  const src = (text || '').replace(/^\ufeff/, '');
  if (!src.trim()) return [];
  const wb = XLSX.read(src, { type: 'string', dense: true, raw: true });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const data = (ws && ws['!data']) || [];
  // Dense rows can be sparse arrays (holes for empty cells) — normalize to strings.
  const grid = data.map((row) => {
    const cells = row || [];
    return Array.from({ length: cells.length }, (_, i) => {
      const cell = cells[i];
      return cell && cell.v !== undefined && cell.v !== null ? String(cell.v) : '';
    });
  });
  while (grid.length && grid[grid.length - 1].every((c) => c === '')) grid.pop();
  return grid;
}

/**
 * Turn a grid into the render model: first row = header, remaining rows = data, padded to a
 * common width, capped, with per-column numeric detection.
 */
export function buildCsvModel(grid) {
  if (!grid || !grid.length) {
    return {
      header: [], rows: [], numericCols: [], totalRows: 0, totalCols: 0,
      truncatedRows: false, truncatedCols: false,
    };
  }
  const totalCols = grid.reduce((m, r) => Math.max(m, r.length), 0);
  const totalRows = grid.length - 1;
  const width = Math.min(totalCols, MAX_CSV_COLS);
  const pad = (row) => Array.from({ length: width }, (_, i) => (row[i] === undefined ? '' : row[i]));
  const header = pad(grid[0]);
  const rowCap = Math.max(1, Math.min(MAX_CSV_ROWS, Math.floor(MAX_CSV_CELLS / Math.max(width, 1))));
  const rows = grid.slice(1, 1 + rowCap).map(pad);
  const numericCols = header.map((_, c) => {
    let numeric = 0;
    let filled = 0;
    for (let r = 0; r < rows.length; r += 1) {
      const v = rows[r][c].trim();
      if (!v) continue; // eslint-disable-line no-continue
      filled += 1;
      if (NUMERIC_RE.test(v)) numeric += 1;
    }
    return filled > 0 && numeric / filled >= NUMERIC_COLUMN_SHARE;
  });
  return {
    header, rows, numericCols, totalRows, totalCols,
    truncatedRows: totalRows > rows.length,
    truncatedCols: totalCols > width,
  };
}

const plural = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

/** Footer text: dimensions, or what was cut when the file exceeds the caps. */
export function formatCsvSummary(model) {
  const { rows, header, totalRows, totalCols, truncatedRows, truncatedCols } = model;
  if (!truncatedRows && !truncatedCols) {
    return `${plural(totalRows, 'row')} × ${plural(totalCols, 'column')}`;
  }
  const parts = [];
  if (truncatedRows) parts.push(`the first ${rows.length.toLocaleString()} of ${plural(totalRows, 'row')}`);
  if (truncatedCols) parts.push(`${header.length} of ${plural(totalCols, 'column')}`);
  return `Showing ${parts.join(' and ')}. Download for the full file.`;
}

const CsvPreview = ({ content }) => {
  const [model, setModel] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setModel(null);
    setError(null);
    import('xlsx')
      .then((XLSX) => {
        if (cancelled) return;
        setModel(buildCsvModel(parseCsvGrid(content, XLSX)));
      })
      .catch((err) => {
        console.error('[CsvPreview] parse failed', err);
        if (!cancelled) setError('Could not parse this file as CSV.');
      });
    return () => {
      cancelled = true;
    };
  }, [content]);

  if (error) return <div className="rich-code-block-csv-status">{error}</div>;
  if (!model) return <div className="rich-code-block-csv-status">Loading table…</div>;
  if (!model.header.length) return <div className="rich-code-block-csv-status">Empty file</div>;

  const colClass = (c) => (model.numericCols[c] ? 'rich-code-block-csv-num' : undefined);

  return (
    <div className="rich-code-block-csv-scroll">
      <table className="rich-code-block-csv-table">
        <thead>
          <tr>
            <th className="rich-code-block-csv-gutter" aria-label="Row number" />
            {model.header.map((h, c) => (
              <th key={c} className={colClass(c)} title={h || undefined}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {model.rows.map((row, r) => (
            <tr key={r}>
              <td className="rich-code-block-csv-gutter">{r + 1}</td>
              {row.map((cell, c) => (
                <td key={c} className={colClass(c)} title={cell || undefined}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="rich-code-block-csv-footer">{formatCsvSummary(model)}</div>
    </div>
  );
};

export default CsvPreview;
